import React, { useMemo, useContext } from 'react';
import Icon from 'src/components/Icon';
import { Input, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledDropdown } from 'reactstrap';
import Notification from '../Notification';
import Loader from '../Loader';
import { Trans } from '@lingui/react/macro';
import { useSideChannelSubscription } from '../../util/useSideChannel';
import getPathFromId from '../../util/getPathFromId';
import useOptionsLookup from './hooks/useOptionsLookup';
import { AuthContext } from '../../context/AuthContext';
import { debounce } from 'lodash';

/**
 * RJSF custom field that renders a list of key-value assignments.
 *
 * Keys are sourced from a lookup (or static options list). Each selected key gets
 * a text input for its value. The output is an array of objects shaped by
 * `keyField` and `valueField` schema props.
 *
 * Schema props:
 *   keyField   - field name used for the key ID in each output object (default: 'id')
 *   valueField - field name used for the value in each output object (default: 'value')
 *   lookup     - same lookup config accepted by LookupFormField:
 *                  { api, jnx, id, label, color, resource, params, options }
 */
function KeyValueAssignmentField(props) {
  const {
    formData,
    registry,
    schema: { title, lookup, keyField = 'id', valueField = 'value', readOnly },
    required,
    onChange: propOnChange,
    fieldPathId: { $id, path: fieldPath },
  } = props;

  const auth = useContext(AuthContext);
  const formContext = registry?.formContext;
  const { sideChannel } = formContext || {};

  const { resource = 'Options', id: idField, label: labelField, color: colorField } = lookup || {};

  const rootFormData = useSideChannelSubscription(sideChannel, 0);
  const dataPath = useMemo(() => getPathFromId($id), [$id]);

  const { options, loadingOptions, errorLoadingOptions } = useOptionsLookup({
    lookup,
    rootFormData,
    path: dataPath,
    auth,
  });

  // Keyed by string ID for fast lookup regardless of number/string type
  const optionsById = useMemo(() => Object.fromEntries((options || []).map(item => [String(item[idField]), item])), [options, idField]);

  const assignments = formData || [];

  const assignedIds = useMemo(() => new Set(assignments.map(a => String(a[keyField]))), [assignments, keyField]);

  const availableOptions = useMemo(() => (options || []).filter(item => !assignedIds.has(String(item[idField]))), [options, assignedIds, idField]);

  function addAssignment(rawId) {
    const item = optionsById[String(rawId)];
    if (!item) return;
    const newAssignment = {
      [keyField]: item[idField],
      [valueField]: '',
      ...(colorField ? { color: item[colorField] } : {}),
    };
    propOnChange([...assignments, newAssignment], fieldPath);
  }

  const updateValue = debounce(
    (idx, newValue) => {
      const updated = assignments.map((a, i) => (i === idx ? { ...a, [valueField]: newValue } : a));
      propOnChange(updated, fieldPath);
    },
    250,
    { trailing: true }
  );

  function removeAssignment(idx) {
    propOnChange(
      assignments.filter((_, i) => i !== idx),
      fieldPath
    );
  }

  return (
    <div className="form-group">
      <label className="control-label">
        {title}
        {required && <span className="required">*</span>}
      </label>

      {loadingOptions ? (
        <Loader dots inline>
          <Trans>Loading</Trans> {resource}
        </Loader>
      ) : errorLoadingOptions ? (
        <Notification color="danger">
          <div>{errorLoadingOptions.message}</div>
        </Notification>
      ) : (
        <>
          {assignments.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
              {assignments.map((assignment, idx) => {
                const item = optionsById[String(assignment[keyField])];
                const color = (colorField && item?.[colorField]) || assignment.color || '#6c757d';
                const label = item?.[labelField] ?? String(assignment[keyField]);
                return (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="badge" style={{ backgroundColor: color, color: '#fff', minWidth: 80, textAlign: 'center', padding: '4px 8px' }}>
                      {label}
                    </span>
                    <Input
                      type="text"
                      bsSize="sm"
                      defaultValue={assignment[valueField] || ''}
                      onChange={e => {
                        e.persist();
                        updateValue(idx, e.target.value);
                      }}
                      disabled={readOnly}
                      placeholder="Value..."
                      style={{ flex: 1 }}
                    />
                    {!readOnly && (
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeAssignment(idx)}>
                        <Icon icon="X" weight="regular" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!readOnly &&
            (availableOptions.length === 0 ? (
              <small>
                <em>
                  <Trans>All form tags assigned</Trans>
                </em>
              </small>
            ) : (
              <UncontrolledDropdown>
                <DropdownToggle color="primary" size="sm" caret>
                  <Icon icon="Plus" weight="regular" /> <Trans>Add tag key</Trans>
                </DropdownToggle>
                <DropdownMenu>
                  {availableOptions.map(item => (
                    <DropdownItem key={item[idField]} onClick={() => addAssignment(item[idField])}>
                      {item[labelField]}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </UncontrolledDropdown>
            ))}
        </>
      )}
    </div>
  );
}

export default KeyValueAssignmentField;
